import {
  getTokenLocalStorage,
  getTokenSessionStorage,
  setTokenLocalStorage,
  setTokenSessionStorage,
  getOrganizationId,
} from '@config/storage';
import { RootState, store } from '@redux/store';
import {
  GraphQLClient,
  RequestMiddleware,
  ResponseMiddleware,
} from 'graphql-request';
import { toast } from 'react-toastify';
import { reportGraphQLErrors } from '@config/errorReporting';
import {
  hasTrialExpiredError,
  notifyTrialExpired,
} from '@config/trialExpiredNotice';

// Pages where we should NOT auto-logout on auth errors (user might be in suspended/disabled state)
const NO_LOGOUT_PATHS = [
  '/organization-suspended',
  '/organization-disabled',
  '/user-disabled',
  '/no-organization',
];

const responseMiddleware: ResponseMiddleware = async (response: any) => {
  // First, before any of the early returns below — otherwise errors on the
  // logout and suspended-state paths would never be reported.
  reportGraphQLErrors(response);

  const refreshed = response?.headers?.get('Refreshed');
  if (refreshed) {
    const state: RootState = store.getState();
    const rememberMe = state.user?.rememberMe;
    if (rememberMe) {
      setTokenLocalStorage(refreshed);
    } else {
      setTokenSessionStorage(refreshed);
    }
  }

  // Don't auto-logout if user is on a page that handles suspended/disabled states
  const currentPath = window.location.pathname;
  if (NO_LOGOUT_PATHS.includes(currentPath)) {
    return;
  }

  // Check for UNAUTHENTICATED GraphQL errors (e.g., expired refresh token, deleted user)
  // Note: FORBIDDEN errors should NOT trigger logout - they mean the user is authenticated
  // but lacks permission for a specific resource. Components should handle FORBIDDEN gracefully.
  const errors = response?.errors || response?.response?.errors;
  if (
    errors?.some(
      (e: any) => e.extensions?.code === 'UNAUTHENTICATED',
    )
  ) {
    // Not a React context — no `useTranslation()` hook available here, so read the current
    // locale bundle straight off the store the way `requestMiddleware` below already does.
    const state: RootState = store.getState();
    toast.info(state.locale.locale.actionMessages.sessionExpiredSignInAgain);
    window.location.href = '/logout';
    return;
  }

  // The trial paywall refuses every mutation from a lapsed organization. Handled here
  // rather than per-hook because it applies app-wide, and several hooks swallow their own
  // errors — which is why a blocked write used to fail with no feedback at all.
  if (hasTrialExpiredError(errors)) {
    notifyTrialExpired();
    return;
  }

  // Only redirect to logout on HTTP 401 Unauthorized status
  // HTTP 403 Forbidden should not trigger logout - user is authenticated but lacks permission
  try {
    const status = response?.status || response?.response?.status;
    if (status === 401) {
      window.location.href = '/logout';
      return;
    }
  } catch (_) {
    // no-op
  }
};

const requestMiddleware: RequestMiddleware = async (request) => {
  const state: RootState = store.getState();
  const rememberMe = state.user?.rememberMe;
  const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();
  // Get organizationId from any available storage (localStorage or sessionStorage)
  const organizationId = getOrganizationId();

  const headers: HeadersInit = {
    ...(request.headers as Record<string, string>),
    Authorization: 'Bearer ' + token,
    // Lets the backend count operations and failures per client, which is what
    // the Web row on the super-admin platform status screen reports. The row
    // cannot be derived any other way: "the web app is up" is already proven by
    // anyone loading it — whether its API calls are succeeding is the part that
    // is not self-evident.
    'x-client': 'web',
  };

  if (organizationId) {
    (headers as Record<string, string>)['X-Organization-ID'] = organizationId;
  }

  return {
    ...request,
    headers,
  };
};

export { gql } from 'graphql-request';

export const graphQlClient = new GraphQLClient(
  process.env.REACT_APP_BACKEND_URL + 'graphql',
  {
    fetch,
    requestMiddleware,
    responseMiddleware,
  },
);

// we might need to migrate to a different graphql library so that we don't need to use this separately
// and that the mutation requests are more descriptive. This will eventually reach a limitation.
export const graphQlUpload = async (
  file: File,
  entity: string,
  query: string,
  variables?: {},
) => {
  const MAX_FILE_SIZE_MB = 50;
  if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
    throw new Error('File size exceeds the maximum allowed size');
  }
  const state: RootState = store.getState();
  const rememberMe = state.user?.rememberMe;
  const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();
  const organizationId = getOrganizationId();

  const operations = {
    query,
    variables: {
      [entity]: {
        ...variables,
        file: null, // This will be replaced in the 'map' with the actual file location.
      },
    },
  };

  const map = {
    '0': [`variables.${entity}.file`],
  };

  const formData = new FormData();
  formData.append('operations', JSON.stringify(operations));
  formData.append('map', JSON.stringify(map));
  formData.append('0', file); // The file should be appended last.

  const headers: Record<string, string> = {
    'apollo-require-preflight': 'true',
    Authorization: 'Bearer ' + token,
  };

  if (organizationId) {
    headers['X-Organization-ID'] = organizationId;
  }

  const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'graphql', {
    method: 'POST',
    body: formData,
    headers,
  });
  const responseData = await response.json();
  if (responseData?.errors?.length || !response.ok) {
    reportGraphQLErrors(
      responseData?.errors?.length ? responseData : { response: { status: response.status } },
      { source: 'upload' },
    );
  }
  return responseData.data;
};

export const graphQlMultipleUpload = async (
  files: File[],
  entity: string,
  query: string,
  variables?: {},
) => {
  const MAX_FILE_SIZE_MB = 50;
  files.forEach((file) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      throw new Error(
        `File '${file.name}' size exceeds the maximum allowed size`,
      );
    }
  });

  const state: RootState = store.getState();
  const rememberMe = state.user?.rememberMe;
  const token = rememberMe ? getTokenLocalStorage() : getTokenSessionStorage();
  const organizationId = getOrganizationId();

  const operations = {
    query,
    variables: {
      [entity]: {
        ...variables,
        files: Array(files.length).fill(null), // Placeholder for the files
      },
    },
  };

  // Dynamically map file indices
  const map: { [key: string]: string[] } = {};
  files.forEach((_, index) => {
    map[index] = [`variables.${entity}.files.${index}`];
  });

  const formData = new FormData();
  formData.append('operations', JSON.stringify(operations));
  formData.append('map', JSON.stringify(map));

  // Append all the files dynamically
  files.forEach((file, index) => {
    formData.append(`${index}`, file);
  });

  const headers: Record<string, string> = {
    'apollo-require-preflight': 'true',
    Authorization: 'Bearer ' + token,
  };

  if (organizationId) {
    headers['X-Organization-ID'] = organizationId;
  }

  const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'graphql', {
    method: 'POST',
    body: formData,
    headers,
  });
  const responseData = await response.json();
  if (responseData?.errors?.length || !response.ok) {
    reportGraphQLErrors(
      responseData?.errors?.length ? responseData : { response: { status: response.status } },
      { source: 'upload' },
    );
  }
  return responseData.data;
};
